import React, { useState, useEffect } from "react";
import s from "./pricesOverview.scss";
import { t } from "i18next";
import Button from "Components/button/Button";
import { Icon, stars0, stars1, stars2, stars3, medal, starLocked, star } from "Icons/index";
import { CircularProgressbarWithChildren, buildStyles } from "react-circular-progressbar";
import cx from "classnames";
import TranslatedString from "Components/TranslatedString";

const TIERS = [
  [10, 49],
  [50, 99],
  [100, 199],
];

const getLastTierMax = () => TIERS[TIERS.length - 1][1];
const isLastTierActive = (current) => current > TIERS[1][1] && current < TIERS[2][1];

const renderStars = (activeTier, current) => {
  if (current > getLastTierMax()) return medal;

  if (!activeTier || !current) return stars0;

  if (activeTier === TIERS[0][0]) {
    return stars1;
  } else if (activeTier === TIERS[1][0]) {
    return stars2;
  } else if (activeTier === TIERS[2][0]) {
    return stars3;
  }
};

const PricesOverview = ({ current, hasCustomTier, currentTier, tierMin, isPromo, isAnnual, isReseller, className }) => {
  const [percentage, setPercentage] = useState(0);
  const [activeTier, setActiveTier] = useState(undefined);

  if (currentTier === "M0_0_5" && activeTier) {
    return setActiveTier(null);
  }

  useEffect(() => {
    const tier = TIERS.find(([min, max]) => {
      return current >= min && current <= max;
    });

    if (!tier) return setActiveTier(undefined);
    setActiveTier(tier[0]);

    // calculate percentage from current
    const percentage = ((current - tier[0]) * 100) / (tier[1] - tier[0]);

    setPercentage(percentage);
  }, [current]);

  if (isAnnual) {
    return (
      <div className={cx(s.container, className)}>
        <p
          className={s.msg}
          dangerouslySetInnerHTML={{ __html: t("prices.howManyAnnual", { count: current }) }}
        ></p>
      </div>
    );
  } else if (!isReseller) {
    return (
      <div className={cx(s.container, className)}>
        <p
          className={s.msg}
          dangerouslySetInnerHTML={{
            __html: t("prices.howManyNotReseller", { count: current, min: tierMin || 5 }),
          }}
        ></p>
      </div>
    );
  }

  if (hasCustomTier) {
    return (
      <div className={cx(s.container, s.horizontalContainer, className)}>
        <Icon svg={medal} width="84px" height="40px" />
        <div>
          <p
            className={s.customTierMsg}
            dangerouslySetInnerHTML={{ __html: t("prices.customTierMsg", { count: current }) }}
          ></p>

          {isPromo && <p className={s.customTierMsg} dangerouslySetInnerHTML={{ __html: t("prices.promoMsg") }}></p>}

          {tierMin && (
            <p
              className={cx(s.customTierMsg, s.tierMinMsg)}
              dangerouslySetInnerHTML={{ __html: t("prices.tierMinMsg", { min: tierMin }) }}
            ></p>
          )}
        </div>
      </div>
    );
  }

  return (
    <>
      <div className={cx(s.container, className)}>
        <Icon svg={renderStars(activeTier, current)} width="84px" height="40px" className={s.stars} />
        {current < 5 ? (
          <p
            className={s.msg}
            dangerouslySetInnerHTML={{ __html: t("prices.howManyLessThan5", { count: current }) }}
          ></p>
        ) : (
          <p className={s.msg} dangerouslySetInnerHTML={{ __html: t("prices.howMany", { count: current }) }}></p>
        )}
        {isLastTierActive(current) ? (
          <p className={s.msg} dangerouslySetInnerHTML={{ __html: t("prices.howManySubtitleLastTier") }}></p>
        ) : current > getLastTierMax() ? null : (
          <p className={s.msg} dangerouslySetInnerHTML={{ __html: t("prices.howManySubtitle") }}></p>
        )}

        <div className={s.progressContainer}>
          {TIERS.map((tier) => {
            const isActive = tier[0] === activeTier;
            const isCompleted = tier[1] < activeTier || current > getLastTierMax();
            const fromNextStep = tier[1] + 1 - current;

            return (
              <CircularProgressbarWithChildren
                className={cx({
                  [s.lockedProgress]: !isActive,
                  [s.focusedProgress]: isActive,
                  [s.completedProgress]: isCompleted,
                })}
                value={isActive ? percentage : isCompleted ? 100 : 0}
                strokeWidth="10"
                styles={buildStyles({
                  // Colors
                  pathColor: `#2c97ff`,
                  trailColor: "#d6d6d6",
                  backgroundColor: "#3e98c7",
                })}
                key={tier[0]}
              >
                {isActive ? (
                  <>
                    <Icon svg={star} width="32px" />
                    <div className={s.rangeLabel}>
                      {tier[0]} - {tier[1]}
                    </div>

                    <p
                      className={s.missingMsg}
                      dangerouslySetInnerHTML={{ __html: t("prices.missing", { missing: fromNextStep }) }}
                    ></p>
                  </>
                ) : (
                  <>
                    <Icon svg={isCompleted ? star : starLocked} width="32px" />
                    <div className={s.rangeLabel}>
                      {tier[0]} - {tier[1]}
                    </div>
                  </>
                )}
              </CircularProgressbarWithChildren>
            );
          })}
        </div>
      </div>

      {current > getLastTierMax() && (
        <div className={s.contactUsCTA}>
          <p>
            <TranslatedString>prices.customQuotaContactUs</TranslatedString>
          </p>
          <Button loading={false}>button.contactUs</Button>
        </div>
      )}
    </>
  );
};

export default PricesOverview;
